/**
 * Монтирование приложения.
 *
 * Отделено от точки входа, чтобы поведение можно было проверить тестом: файл,
 * выполняющий работу при импорте, тестируется только целиком и только один раз
 * за процесс.
 *
 * ПЕРЕВОРОТ блока `WEB-1.1` (спецификация §7). Было: «в корень монтируется
 * экран входа, а после подтверждения кода — лента работ; переходов между ними
 * ровно два, и оба вызваны исходом обращения к серверу. Роутера у блока нет —
 * адрес страницы при этом не меняется». Стало: в корень монтируется
 * **оболочка** — шапка с двумя переключателями плюс место маршрута, — и
 * переходы вызваны как исходом обращения, так и действием человека; адрес
 * страницы при переходе меняется, документ не перезагружается.
 *
 * Монтирование живёт в оболочке, а здесь остались две вещи, которые к ней не
 * относятся: точка входа в дерево документа и показ отказа запуска. Вторая
 * половина прежнего утверждения сохранена целиком: без корневого элемента
 * монтирование падает внятно. Молчаливый выход при отсутствии точки
 * монтирования даёт пустую белую страницу без единого следа в консоли — самая
 * дорогая в отладке ошибка фронтенда.
 *
 * ПЕРЕВОРОТ блока `WEB-3.2` (спецификация §6). Было: «монтирует оболочку».
 * Стало: здесь же **выбор** — публичная страница отслеживания или оболочка
 * кабинета. Выбор живёт в проверяемой функции, а точка входа её только зовёт:
 * точка входа не участвует ни в одном тесте, и ветка, заведённая там, осталась
 * бы непроверенной. На пути ссылки оболочка не монтируется вовсе — ни профиля,
 * ни подписки на хеш; на прочих путях кабинет ведёт себя как прежде.
 */

import type { ApiClient } from './api/client';
import type { Locale, Strings } from './i18n';
import { parseTrackingToken } from './router';
import { mountPublicTracking } from './screens/public-tracking';
import { mountShell, requireRoot, ROOT_SELECTOR } from './shell';

export { ROOT_SELECTOR };

export interface AppDependencies {
  readonly client: ApiClient;
  /**
   * Набор строк выбранного языка.
   *
   * Оболочка берёт его сама по выбранному языку: место, где решается «на каком
   * языке страница», обязано быть одно. Поле остаётся объявленным потому, что
   * зависимость приложения от набора строк никуда не делась, — просто выбирает
   * его теперь язык, а не вызывающий.
   */
  /**
   * Набор строк. **Монтированием не читается** — и это названо, а не оставлено
   * догадке (находка шага REVIEW блока `WEB-1.1`).
   *
   * Строки разрешает оболочка сама, по локали: второе место, где решается «на
   * каком языке страница», разошлось бы с первым молча. Поле живёт потому, что
   * его передаёт запечатанный набор `w0-3a`, и снять его значило бы править
   * печать ради косметики. Мёртвым при этом оно остаётся, и читатель обязан
   * знать об этом из кода, а не выяснять поиском.
   */
  readonly strings: Strings;
  /** Начальное значение языка. */
  readonly locale: Locale;
}

/**
 * Отказ запуска — на экране, а не только в консоли.
 *
 * Ручной прогон 23.08.2026 увидел на шаге 2 ровно то, что чек-лист запрещает:
 * без переменной сборщика консоль громко называла её, а страница оставалась
 * пустой. Громко — значит там, куда смотрит человек; консоль открыта не у
 * всякого, кто разворачивает сборку.
 *
 * Сообщение показывается тем, которое бросил отказ: это не пользовательская
 * строка, а имя переменной или селектора для того, кто собирает и выкладывает
 * приложение (решение `D36`). На пяти языках оно не нужно: в правильно
 * собранном приложении его не увидит никто.
 *
 * Точки монтирования может не быть — это один из двух отказов запуска, —
 * поэтому сообщение ложится в корень, если он есть, и в тело страницы иначе.
 */
export function showStartupFailure(doc: Document, error: unknown): void {
  const notice = doc.createElement('p');

  notice.setAttribute('role', 'alert');
  notice.textContent = error instanceof Error ? error.message : String(error);

  const root = doc.querySelector<HTMLElement>(ROOT_SELECTOR) ?? doc.body;

  root.replaceChildren(notice);
}

export function mountApp(doc: Document, dependencies: AppDependencies): HTMLElement {
  const root = requireRoot(doc);

  mountShell(doc, { client: dependencies.client, locale: dependencies.locale });

  return root;
}

export interface StartOptions {
  readonly client: ApiClient;
  /** Начальное значение языка. Без него — разрешение из браузера. */
  readonly locale?: Locale;
}

/**
 * Запуск приложения: страница по ссылке или кабинет — по пути документа.
 *
 * Путь разбирает маршрутизатор, а не этот файл: второй разборщик адреса
 * разошёлся бы с первым молча. Страница по ссылке к серверу при монтировании
 * не обращается — без PIN показывать нечего; кабинет загружается сразу, как и
 * до этого блока.
 *
 * Функция не асинхронная намеренно: отказ монтирования — нет корня — обязан
 * остаться броском в точке входа, где его показывает отказ запуска, а не
 * отклонённым обещанием, которого там никто не ждёт. Наружу отдаётся загрузка
 * кабинета, когда она есть.
 */
export function startApp(doc: Document, options: StartOptions): Promise<void> | void {
  const root = requireRoot(doc);
  const token = parseTrackingToken(doc.location.pathname);
  const locale = options.locale === undefined ? {} : { locale: options.locale };

  if (token !== null) {
    mountPublicTracking(root, { client: options.client, token, ...locale });

    return;
  }

  return mountShell(doc, { client: options.client, ...locale }).load();
}
